import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, catchError, tap, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MockToggleService } from './mock-toggle.service';
import { MOCK_LOGS, generateMockId } from './mock-data';

export interface LogEntry {
  id: number;
  timestamp: Date;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'IMPORT' | 'EXPORT';
  entity: string;
  entityId: string;
  user: string;
  description: string;
}

interface ApiLogEntry {
  id: number;
  timestamp: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'IMPORT' | 'EXPORT';
  entity: string;
  entity_id?: string;
  entityId?: string;
  user: string;
  description: string;
}

export interface PaginatedLogsResponse<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface LogsPaginatedQuery {
  page?: number;
  pageSize?: number;
  action?: 'CREATE' | 'UPDATE' | 'DELETE';
  entity?: string;
  user?: string;
  dictionaryId?: number;
  dictionaryName?: string;
  startDate?: string;
  endDate?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LogsService {
  private http = inject(HttpClient);
  private mockToggle = inject(MockToggleService);
  
  private logsData = signal<LogEntry[]>([]);

  private normalizeLog(apiLog: ApiLogEntry): LogEntry {
    return {
      id: typeof apiLog.id === 'number' ? apiLog.id : Number(apiLog.id),
      timestamp: new Date(apiLog.timestamp),
      action: apiLog.action,
      entity: apiLog.entity,
      entityId: String(apiLog.entity_id ?? apiLog.entityId ?? ''),
      user: apiLog.user,
      description: apiLog.description
    };
  }

  private useMock(): boolean {
    return this.mockToggle.isMockEnabled();
  }

  private initMockLogs(): LogEntry[] {
    const data = MOCK_LOGS.map(log => ({ ...log, timestamp: new Date(log.timestamp) }));
    this.logsData.set(data);
    return data;
  }

  getLogs(): Observable<LogEntry[]> {
    if (this.useMock()) {
      const logs = this.initMockLogs();
      return of(logs.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()));
    }

    return this.http.get<ApiLogEntry[]>(`${environment.apiUrl}/logs`).pipe(
      map(logs => logs.map(log => this.normalizeLog(log))),
      map(logs => logs.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())),
      tap(logs => this.logsData.set(logs)),
      catchError(error => {
        console.error('Error fetching logs from API:', error);
        return of([]);
      })
    );
  }

  getLogsPaginated(query: LogsPaginatedQuery = {}): Observable<PaginatedLogsResponse<LogEntry>> {
    const page = query.page ?? 1;
    const pageSize = query.pageSize ?? 100;

    if (this.useMock()) {
      const logs = this.initMockLogs().sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      const filtered = this.filterLogsForDictionary(logs, query.dictionaryId, query.dictionaryName);
      const total = filtered.length;
      const totalPages = total > 0 ? Math.ceil(total / pageSize) : 0;
      const boundedPage = totalPages > 0 ? Math.min(Math.max(page, 1), totalPages) : 1;
      const start = (boundedPage - 1) * pageSize;
      return of({
        items: filtered.slice(start, start + pageSize),
        page: boundedPage,
        pageSize,
        total,
        totalPages,
      });
    }

    const params: Record<string, string> = {
      page: String(page),
      pageSize: String(pageSize),
    };
    if (query.action) params['action'] = query.action;
    if (query.entity) params['entity'] = query.entity;
    if (query.user) params['user'] = query.user;
    if (query.dictionaryId !== undefined) params['dictionaryId'] = String(query.dictionaryId);
    if (query.dictionaryName) params['dictionaryName'] = query.dictionaryName;
    if (query.startDate) params['startDate'] = query.startDate;
    if (query.endDate) params['endDate'] = query.endDate;

    return this.http
      .get<PaginatedLogsResponse<ApiLogEntry>>(`${environment.apiUrl}/logs/paginated`, { params })
      .pipe(
        map((response) => ({
          ...response,
          items: response.items.map((log) => this.normalizeLog(log)),
        })),
        catchError((error) => {
          console.error('Error fetching paginated logs from API:', error);
          return of({
            items: [],
            page,
            pageSize,
            total: 0,
            totalPages: 0,
          });
        }),
      );
  }

  get logs() {
    return this.logsData;
  }

  addLog(entry: Omit<LogEntry, 'id' | 'timestamp' | 'entityId'> & { entityId: string | number }): void {
    const entityId = String(entry.entityId);
    if (this.useMock()) {
      this.mockAddLog({ ...entry, entityId });
      return;
    }

    const payload = {
      action: entry.action,
      entity: entry.entity,
      entity_id: entityId,
      user: entry.user,
      description: entry.description
    };

    this.http.post<ApiLogEntry>(`${environment.apiUrl}/logs`, payload).pipe(
      map(log => this.normalizeLog(log)),
      tap(newLog => {
        this.logsData.update(logs => [newLog, ...logs]);
      }),
      catchError(error => {
        console.error('Error adding log to API:', error);
        return of(null);
      })
    ).subscribe();
  }

  private mockAddLog(entry: Omit<LogEntry, 'id' | 'timestamp'>): void {
    const newLog: LogEntry = {
      ...entry,
      entityId: String(entry.entityId),
      id: generateMockId('log-'),
      timestamp: new Date(),
    };
    this.logsData.update(logs => [newLog, ...logs]);
  }

  private filterLogsForDictionary(
    logs: LogEntry[],
    dictionaryId?: number,
    dictionaryName?: string,
  ): LogEntry[] {
    if (dictionaryId === undefined) {
      return logs;
    }

    const dictIdStr = String(dictionaryId);
    const normalizedName = (dictionaryName ?? '').trim().toLowerCase();
    return logs.filter((log) => {
      if (log.entityId === dictIdStr) {
        return true;
      }

      if (log.entity === 'Field' && normalizedName) {
        const desc = log.description.toLowerCase();
        return desc.includes(`"${normalizedName}"`) || desc.includes(`'${normalizedName}'`);
      }

      return false;
    });
  }
}
